import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { addFavoriteCommentAction } from '../../actions/favoriteProductsActions';
import {
    deleteCommentAction,
    deleteMultipleProductsAction,
    deleteProductAction,
    getProjectProductsAction,
    getLeedDocumentTypesAction,
    getLeedLEMDocumentTypesAction,
    getDocumentTypesByGroupAction,
    getAllActiveDocumentTypesAction,
    syncDocumentTypesAction,
} from '../../actions/productActions';
import { GetDocumentTypesResponse, LeedDocumentType } from '../../actions/productActions/types';
import { getBrandByIdAction } from '../../actions/transparencyCatalogActions';

export interface ProductReducerType {
    pending: boolean;
    packageId?: string;
    filePath?: string;
    leedDocumentTypes: LeedDocumentType[] | null;
    leedLemDocumentTypes: LeedDocumentType[] | null;
    documentTypesByGroup: Record<number, GetDocumentTypesResponse[]>;
    allActiveDocumentTypes: GetDocumentTypesResponse[] | null;
}

const initialState: ProductReducerType = {
    pending: false,
    packageId: undefined,
    filePath: undefined,
    leedDocumentTypes: null,
    documentTypesByGroup: {},
    allActiveDocumentTypes: null,
};

export const productSlice = createSlice({
    name: 'product',
    initialState: initialState,
    reducers: {
        setPackageId(state, action: PayloadAction<string | undefined>) {
            state.packageId = action.payload;
        },
        setFilePath(state, action: PayloadAction<string | undefined>) {
            state.filePath = action.payload;
        },
        clearProductSlice(state) {
            state.pending = false;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getProjectProductsAction.pending, (state) => {
            state.pending = true;
        });
        builder.addCase(getProjectProductsAction.fulfilled, (state) => {
            state.pending = false;
        });
        builder.addCase(getProjectProductsAction.rejected, (state) => {
            state.pending = false;
        });
        builder.addCase(deleteProductAction.pending, (state) => {
            state.pending = true;
        });
        builder.addCase(deleteProductAction.fulfilled, (state) => {
            state.pending = false;
        });
        builder.addCase(deleteProductAction.rejected, (state) => {
            state.pending = false;
        });
        builder.addCase(deleteMultipleProductsAction.pending, (state) => {
            state.pending = true;
        });
        builder.addCase(deleteMultipleProductsAction.fulfilled, (state) => {
            state.pending = false;
        });
        builder.addCase(deleteMultipleProductsAction.rejected, (state) => {
            state.pending = false;
        });
        builder.addCase(addFavoriteCommentAction.pending, (state) => {
            state.pending = true;
        });
        builder.addCase(addFavoriteCommentAction.fulfilled, (state) => {
            state.pending = false;
        });
        builder.addCase(addFavoriteCommentAction.rejected, (state) => {
            state.pending = false;
        });
        builder.addCase(deleteCommentAction.pending, (state) => {
            state.pending = true;
        });
        builder.addCase(deleteCommentAction.fulfilled, (state) => {
            state.pending = false;
        });
        builder.addCase(deleteCommentAction.rejected, (state) => {
            state.pending = false;
        });
        builder.addCase(getBrandByIdAction.pending, (state) => {
            state.pending = true;
        });
        builder.addCase(getBrandByIdAction.fulfilled, (state) => {
            state.pending = false;
        });
        builder.addCase(getBrandByIdAction.rejected, (state) => {
            state.pending = false;
        });
        // Cache LEED document types
        builder.addCase(getLeedDocumentTypesAction.fulfilled, (state, action) => {
            if (action.payload?.payload?.data) {
                state.leedDocumentTypes = action.payload.payload.data;
            }
        });
        builder.addCase(getLeedLEMDocumentTypesAction.fulfilled, (state, action) => {
            if (action.payload?.payload?.data) {
                state.leedLemDocumentTypes = action.payload.payload.data;
            }
        });
        // Cache document types by group
        builder.addCase(getDocumentTypesByGroupAction.fulfilled, (state, action) => {
            if (action.payload?.payload?.data && action.meta.arg !== undefined) {
                const groupId = action.meta.arg;
                state.documentTypesByGroup[groupId] = action.payload.payload.data;
            }
        });
        // Cache all active document types
        builder.addCase(getAllActiveDocumentTypesAction.fulfilled, (state, action) => {
            const data = (action.payload as any)?.data ?? (action.payload as any)?.payload?.data ?? [];
            if (data) {
                state.allActiveDocumentTypes = data;
            }
        });
        builder.addCase(getAllActiveDocumentTypesAction.rejected, (state) => {
            state.allActiveDocumentTypes = null;
        });
        builder.addCase(syncDocumentTypesAction.pending, (state) => {
            state.allActiveDocumentTypes = null;
        });
        builder.addCase(syncDocumentTypesAction.fulfilled, (state) => {
            state.allActiveDocumentTypes = null;
        });
    },
});

export const { setPackageId, setFilePath, clearProductSlice } = productSlice.actions;

export default productSlice.reducer;
